import { z } from 'zod';

// ─── Enums ──────────────────────────────────────────────────────────────────

export const MessageGenPhaseSchema = z.enum([
  'idle',
  'gathering_details',
  'awaiting_approval',
  'generating',
  'messages_ready',
  'prioritizing',
  'prioritized',
  'curating',
  'curated',
  'error',
]);

export type MessageGenPhase = z.infer<typeof MessageGenPhaseSchema>;

// ─── Marketing Message ──────────────────────────────────────────────────────

export const MarketingMessageSchema = z.object({
  identifier: z.string(),
  title: z.string(),
  content: z.string(),
  category: z.string(),
  targetAudience: z.string(),
  status: z.enum(['draft', 'approved', 'published']),
  priority: z.number().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  createdOn: z.string(),
  updatedOn: z.string(),
});

export type MarketingMessage = z.infer<typeof MarketingMessageSchema>;

// ─── Chat History Entry ─────────────────────────────────────────────────────

export const MessageGenChatEntrySchema = z.object({
  role: z.enum(['user', 'assistant']),
  content: z.string(),
});

export type MessageGenChatEntry = z.infer<typeof MessageGenChatEntrySchema>;

// ─── Canonical State (Single Source of Truth) ───────────────────────────────

const MAX_CHAT_HISTORY = 20;

export const DEFAULT_MAX_MESSAGES = 200;

/**
 * Allowed objectives for message generation.
 * "Generalized" covers non-topic-specific requests.
 * Any objective outside this list is rejected by the gather-details node.
 */
export const ALLOWED_OBJECTIVES = [
  'Tolerability',
  'Safety',
  'Efficacy',
  'Access',
  'Trial Design',
  'Unmet Needs',
  'Generalized',
] as const;

export type AllowedObjective = (typeof ALLOWED_OBJECTIVES)[number];

export const MessageGenStateSchema = z.object({
  phase: MessageGenPhaseSchema.default('idle'),
  name: z.string().nullable().default(null),
  numberOfMessages: z.number().int().positive().default(50),
  maxMessages: z.number().int().positive().default(DEFAULT_MAX_MESSAGES),
  brandName: z.string().nullable().default(null),
  /** One or more segment IDs to target. Empty array means none selected yet. */
  segmentIds: z.array(z.string()).default([]),
  /** Free-form description of the desired message objective. */
  objective: z.string().nullable().default(null),
  /** Classified topic bucket — null when objective hasn't been set or couldn't be matched. */
  objectiveCategory: z.enum(ALLOWED_OBJECTIVES).nullable().default(null),
  messages: z.array(MarketingMessageSchema).default([]),
  prioritizedMessages: z.array(MarketingMessageSchema).default([]),
  curatedMessages: z.array(MarketingMessageSchema).default([]),
  error: z.string().nullable().default(null),
  chatHistory: z.array(MessageGenChatEntrySchema).default([]),
});

export type MessageGenState = z.infer<typeof MessageGenStateSchema>;

/** Default initial state for a new message generation session. */
export function getDefaultMessageGenState(): MessageGenState {
  return MessageGenStateSchema.parse({});
}

// ─── External Agent Stub Schemas ────────────────────────────────────────────

export const MessageGenerationAgentRequestSchema = z.object({
  sessionId: z.string(),
  segmentIds: z.array(z.string()).nullable().optional(),
  brandName: z.string().nullable().optional(),
  objective: z.string(),
  numberOfMessages: z.number().int().positive(),
});

export type MessageGenerationAgentRequest = z.infer<typeof MessageGenerationAgentRequestSchema>;

export const MessageGenerationAgentResponseSchema = z.object({
  messages: z.array(MarketingMessageSchema),
});

export type MessageGenerationAgentResponse = z.infer<typeof MessageGenerationAgentResponseSchema>;

export const MessagePrioritizationAgentRequestSchema = z.object({
  sessionId: z.string(),
  messages: z.array(MarketingMessageSchema),
});

export type MessagePrioritizationAgentRequest = z.infer<typeof MessagePrioritizationAgentRequestSchema>;

export const MessagePrioritizationAgentResponseSchema = z.object({
  messages: z.array(MarketingMessageSchema),
});

export type MessagePrioritizationAgentResponse = z.infer<typeof MessagePrioritizationAgentResponseSchema>;

export const MessageCurationAgentRequestSchema = z.object({
  sessionId: z.string(),
  segmentIds: z.array(z.string()).nullable().optional(),
  messages: z.array(MarketingMessageSchema),
});

export type MessageCurationAgentRequest = z.infer<typeof MessageCurationAgentRequestSchema>;

export const MessageCurationAgentResponseSchema = z.object({
  messages: z.array(MarketingMessageSchema),
});

export type MessageCurationAgentResponse = z.infer<typeof MessageCurationAgentResponseSchema>;

// ─── Session Schema ─────────────────────────────────────────────────────────

export const MessageGenSessionSchema = z.object({
  id: z.number(),
  sessionId: z.string(),
  state: z.record(z.string(), z.unknown()).transform((v) => v as MessageGenState),
  createdOn: z.date(),
  createdBy: z.number(),
});

export type MessageGenSession = z.infer<typeof MessageGenSessionSchema>;

// ─── Session Summary (for listing) ──────────────────────────────────────────

export const MessageGenSessionSummarySchema = z.object({
  id: z.number(),
  sessionId: z.string(),
  name: z.string().nullable(),
  phase: MessageGenPhaseSchema,
  createdOn: z.string(),
  segmentIds: z.array(z.string()),
});

export type MessageGenSessionSummary = z.infer<typeof MessageGenSessionSummarySchema>;

// ─── Create Session ─────────────────────────────────────────────────────────

export const CreateMessageGenSessionRequestSchema = z.object({
  sessionId: z.string().min(1),
  initialState: z
    .object({
      segmentIds: z.array(z.string()).optional(),
    })
    .optional(),
});

export type CreateMessageGenSessionRequest = z.infer<typeof CreateMessageGenSessionRequestSchema>;

// ─── Actions ────────────────────────────────────────────────────────────────

export const MessageGenActionType = {
  SetDetails: 'SET_DETAILS',
  SetObjectiveCategory: 'SET_OBJECTIVE_CATEGORY',
  RequestApproval: 'REQUEST_APPROVAL',
  ApproveGeneration: 'APPROVE_GENERATION',
  SetGeneratedMessages: 'SET_GENERATED_MESSAGES',
  SetPrioritizedMessages: 'SET_PRIORITIZED_MESSAGES',
  SetCuratedMessages: 'SET_CURATED_MESSAGES',
  UpdateMessage: 'UPDATE_MESSAGE',
  SetError: 'SET_ERROR',
  ClearError: 'CLEAR_ERROR',
  AppendChatHistory: 'APPEND_CHAT_HISTORY',
  SetPhase: 'SET_PHASE',
} as const;

export type MessageGenActionType = (typeof MessageGenActionType)[keyof typeof MessageGenActionType];

export type MessageGenAction =
  | {
      type: typeof MessageGenActionType.SetDetails;
      payload: {
        numberOfMessages?: number;
        brandName?: string | null;
        segmentIds?: string[];
        objective?: string | null;
      };
    }
  | {
      type: typeof MessageGenActionType.SetObjectiveCategory;
      payload: {
        objectiveCategory: AllowedObjective | null;
      };
    }
  | { type: typeof MessageGenActionType.RequestApproval }
  | { type: typeof MessageGenActionType.ApproveGeneration }
  | {
      type: typeof MessageGenActionType.SetGeneratedMessages;
      payload: {
        messages: MarketingMessage[];
      };
    }
  | {
      type: typeof MessageGenActionType.SetPrioritizedMessages;
      payload: {
        messages: MarketingMessage[];
      };
    }
  | {
      type: typeof MessageGenActionType.SetCuratedMessages;
      payload: {
        messages: MarketingMessage[];
      };
    }
  | {
      type: typeof MessageGenActionType.UpdateMessage;
      payload: {
        index: number;
        updates: Partial<MarketingMessage>;
      };
    }
  | { type: typeof MessageGenActionType.SetError; payload: { error: string } }
  | { type: typeof MessageGenActionType.ClearError }
  | {
      type: typeof MessageGenActionType.AppendChatHistory;
      payload: {
        entries: MessageGenChatEntry[];
      };
    }
  | { type: typeof MessageGenActionType.SetPhase; payload: { phase: MessageGenPhase } };

/**
 * Zod schema for actions — used for the frontend action dispatch endpoint.
 * Each action variant is a separate object with a `type` discriminator.
 */
export const MessageGenActionSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal(MessageGenActionType.SetDetails),
    payload: z.object({
      numberOfMessages: z.number().int().positive().optional(),
      brandName: z.string().nullable().optional(),
      segmentIds: z.array(z.string()).optional(),
      objective: z.string().nullable().optional(),
    }),
  }),
  z.object({
    type: z.literal(MessageGenActionType.SetObjectiveCategory),
    payload: z.object({
      objectiveCategory: z.enum(ALLOWED_OBJECTIVES).nullable(),
    }),
  }),
  z.object({ type: z.literal(MessageGenActionType.RequestApproval) }),
  z.object({ type: z.literal(MessageGenActionType.ApproveGeneration) }),
  z.object({
    type: z.literal(MessageGenActionType.SetGeneratedMessages),
    payload: z.object({ messages: z.array(MarketingMessageSchema) }),
  }),
  z.object({
    type: z.literal(MessageGenActionType.SetPrioritizedMessages),
    payload: z.object({ messages: z.array(MarketingMessageSchema) }),
  }),
  z.object({
    type: z.literal(MessageGenActionType.SetCuratedMessages),
    payload: z.object({ messages: z.array(MarketingMessageSchema) }),
  }),
  z.object({
    type: z.literal(MessageGenActionType.UpdateMessage),
    payload: z.object({
      index: z.number().int().min(0),
      updates: MarketingMessageSchema.partial(),
    }),
  }),
  z.object({
    type: z.literal(MessageGenActionType.SetError),
    payload: z.object({ error: z.string() }),
  }),
  z.object({ type: z.literal(MessageGenActionType.ClearError) }),
  z.object({
    type: z.literal(MessageGenActionType.AppendChatHistory),
    payload: z.object({ entries: z.array(MessageGenChatEntrySchema) }),
  }),
  z.object({
    type: z.literal(MessageGenActionType.SetPhase),
    payload: z.object({ phase: MessageGenPhaseSchema }),
  }),
]);

// ─── Reducer ────────────────────────────────────────────────────────────────

/**
 * Pure reducer for MessageGenState.
 * Single source of truth for all state mutations — used by both
 * backend (session persistence) and frontend (optimistic updates).
 */
export function messageGenReducer(state: MessageGenState, action: MessageGenAction): MessageGenState {
  switch (action.type) {
    case MessageGenActionType.SetDetails: {
      const { numberOfMessages, brandName, segmentIds, objective } = action.payload;
      const next = { ...state };
      if (numberOfMessages !== undefined) {
        next.numberOfMessages = Math.min(numberOfMessages, state.maxMessages);
      }
      if (brandName !== undefined) next.brandName = brandName;
      if (segmentIds !== undefined) next.segmentIds = segmentIds;
      if (objective !== undefined) {
        next.objective = objective;
        // Clear category when objective changes — compliance will re-classify
        next.objectiveCategory = null;
      }
      return next;
    }

    case MessageGenActionType.SetObjectiveCategory:
      return { ...state, objectiveCategory: action.payload.objectiveCategory };

    case MessageGenActionType.RequestApproval:
      return { ...state, phase: 'awaiting_approval' };

    case MessageGenActionType.ApproveGeneration:
      return { ...state, phase: 'generating' };

    case MessageGenActionType.SetGeneratedMessages:
      return {
        ...state,
        phase: 'messages_ready',
        messages: action.payload.messages,
        error: null,
      };

    case MessageGenActionType.SetPrioritizedMessages:
      return {
        ...state,
        phase: 'prioritized',
        prioritizedMessages: action.payload.messages,
        error: null,
      };

    case MessageGenActionType.SetCuratedMessages:
      return {
        ...state,
        phase: 'curated',
        curatedMessages: action.payload.messages,
        error: null,
      };

    case MessageGenActionType.UpdateMessage: {
      const { index, updates } = action.payload;
      if (index < 0 || index >= state.messages.length) return state;
      const updatedMessages = [...state.messages];
      updatedMessages[index] = {
        ...updatedMessages[index],
        ...updates,
        updatedOn: new Date().toISOString(),
      };
      return { ...state, messages: updatedMessages };
    }

    case MessageGenActionType.SetError:
      return { ...state, phase: 'error', error: action.payload.error };

    case MessageGenActionType.ClearError:
      return { ...state, error: null };

    case MessageGenActionType.AppendChatHistory: {
      const merged = [...state.chatHistory, ...action.payload.entries];
      return { ...state, chatHistory: merged.slice(-MAX_CHAT_HISTORY) };
    }

    case MessageGenActionType.SetPhase:
      return { ...state, phase: action.payload.phase };

    default:
      return state;
  }
}

/**
 * Apply a sequence of actions to a state, returning the final state.
 */
export function applyMessageGenActions(state: MessageGenState, actions: MessageGenAction[]): MessageGenState {
  return actions.reduce(messageGenReducer, state);
}

// ─── Chat Message Request ───────────────────────────────────────────────────

export const MessageGenChatRequestSchema = z.object({
  messageGenSessionId: z.number().int().positive(),
  sessionId: z.string().min(1),
  message: z.string().min(1),
});

export type MessageGenChatRequest = z.infer<typeof MessageGenChatRequestSchema>;

// ─── Actions Dispatch Request ───────────────────────────────────────────────

export const MessageGenDispatchActionsRequestSchema = z.object({
  actions: z.array(MessageGenActionSchema).min(1),
});

export type MessageGenDispatchActionsRequest = z.infer<typeof MessageGenDispatchActionsRequestSchema>;
